import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Loader2, PlayIcon, Terminal, Copy, Check, AlertCircle, Minus, Plus, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { toastSomethingError } from '@/components/ui/toast-content';
import { useGPUInstanceStore } from '@/store/gpu-instance';
import { runSSHInstanceSSE } from '@/service/gpu-instance';
import {
  parseSSEStream,
  SSETaskIdData,
  SSEStatusData,
  SSECompleteData,
  SSEErrorData,
  SSESSHKeyData,
} from '@/utils/sse';
import { useGetServerlessInstancePricing } from '@/service/serverless-gpu/hook';
import { useGetUserBalance } from '@/service/payment/hook';
import { Link } from 'react-router-dom';
import PATH_PAGE from '@/router/path';
import { useQuickTopup } from '@/hooks/useQuickTopup';
import { QuickTopupDialog } from '@/components/quick-topup/QuickTopupDialog';
import { fCurrencyUSDExtended } from '@/utils/formatNumber';
import SSHKeyModal from '../components/SSHKeyModal';
import FrameworkSelector from '../components/FrameworkSelector';
import ModelMultiSelector from '../components/ModelMultiSelector';
import StackDeploymentInfoCard from '../components/StackDeploymentInfoCard';
import TrainingConfigSelector from '../components/TrainingConfigSelector';
import TrainingSetupInfoCard from '../components/TrainingSetupInfoCard';
import LabelConfigSelector, { LabelingTool } from '../components/LabelConfigSelector';
import AIAppSelector, { AIAppType } from '../components/AIAppSelector';
import useParamWorkspaceId from '@/hook/useParamWorkspaceId';
import { DemoOnly } from '@/components/DemoOnly';
import { useDemoMode } from '@/context/DemoModeContext';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  mockStackDeploymentInfo,
  StackDeploymentInfo,
  SelectedModel,
  mockTrainingSetupInfo,
  TrainingSetupInfo,
  calculateRecommendedVolumeSize,
  DeploymentFramework,
  DEPLOYABLE_MODELS,
  autoAssignMigProfile,
} from '@/mock/gpu-instance';
import { useDeepLinkIntent } from '@/hooks/useDeepLink';

// MIG profiles for mixed config (must total 7 slices per GPU)
const MIG_PROFILES_FOR_MIXING = [
  { id: '1g.18gb', name: '1g.18gb', memory: 18, slices: 1, smCount: 19, isMemoryOptimized: false },
  { id: '1g.35gb', name: '1g.35gb', memory: 35, slices: 1, smCount: 19, isMemoryOptimized: true },
  { id: '2g.35gb', name: '2g.35gb', memory: 35, slices: 2, smCount: 38, isMemoryOptimized: false },
  { id: '3g.71gb', name: '3g.71gb', memory: 71, slices: 3, smCount: 57, isMemoryOptimized: true },
  { id: '4g.71gb', name: '4g.71gb', memory: 71, slices: 4, smCount: 76, isMemoryOptimized: false },
];

// Base VM configuration ranges (Demo only)
const GPU_COUNT_CONFIG = {
  min: 1,
  max: 8,
  step: 1,
  default: 1,
};

const CPU_CONFIG = {
  min: 1,
  max: 128,
  step: 1,
  default: 8,
};

const RAM_CONFIG = {
  min: 1,
  max: 1024,
  step: 1,
  default: 64,
};

const IMAGE_OPTIONS = [
  {
    id: 'ubuntu-22.04-cuda-12.4',
    name: 'Ubuntu 22.04 + CUDA 12.4',
    description: 'Ubuntu 22.04 LTS with NVIDIA CUDA 12.4, cuDNN 9.0',
    recommended: true,
  },
  {
    id: 'ubuntu-22.04-cuda-12.2',
    name: 'Ubuntu 22.04 + CUDA 12.2',
    description: 'Ubuntu 22.04 LTS with NVIDIA CUDA 12.2, cuDNN 8.9',
  },
  {
    id: 'ubuntu-22.04-cuda-11.8',
    name: 'Ubuntu 22.04 + CUDA 11.8',
    description: 'Ubuntu 22.04 LTS with NVIDIA CUDA 11.8, cuDNN 8.6 (Legacy)',
  },
  {
    id: 'ubuntu-24.04-cuda-12.4',
    name: 'Ubuntu 24.04 + CUDA 12.4',
    description: 'Ubuntu 24.04 LTS with NVIDIA CUDA 12.4, cuDNN 9.0',
  },
  {
    id: 'pytorch-2.3-cuda-12.4',
    name: 'PyTorch 2.3 + CUDA 12.4',
    description: 'Pre-configured PyTorch 2.3 with CUDA 12.4, torchvision, torchaudio',
  },
  {
    id: 'tensorflow-2.16-cuda-12.3',
    name: 'TensorFlow 2.16 + CUDA 12.3',
    description: 'Pre-configured TensorFlow 2.16 with CUDA 12.3, Keras 3',
  },
];

// Preset mixed configurations (total memory must not exceed 141GB for H200)
const PRESET_MIXED_CONFIGS = [
  {
    id: 'max-vram',
    name: 'Max VRAM (3+4)',
    description: '1x 3g.71gb + 1x 4g.71gb = 142GB total (maximum possible)',
    config: { '3g.71gb': 1, '4g.71gb': 1 },
    totalMemory: 142,
    isRecommended: true,
  },
  {
    id: 'balanced',
    name: 'Balanced (4+2+1)',
    description: '1x 4g.71gb + 1x 2g.35gb + 1x 1g.18gb = 124GB total',
    config: { '4g.71gb': 1, '2g.35gb': 1, '1g.18gb': 1 },
    totalMemory: 124,
  },
  {
    id: 'inference-multi',
    name: 'Multi-Inference (4+3×1)',
    description: '1x 4g.71gb + 3x 1g.18gb = 125GB (1 large + 3 small)',
    config: { '4g.71gb': 1, '1g.18gb': 3 },
    totalMemory: 125,
  },
  {
    id: 'dual-medium',
    name: 'Dual Medium (2×2+3×1)',
    description: '2x 2g.35gb + 3x 1g.18gb = 124GB (2 medium + 3 small)',
    config: { '2g.35gb': 2, '1g.18gb': 3 },
    totalMemory: 124,
  },
  {
    id: 'max-instances',
    name: 'Max Instances (7×1)',
    description: '7x 1g.18gb = 126GB (maximum parallel workloads)',
    config: { '1g.18gb': 7 },
    totalMemory: 126,
  },
];

const GPUInstanceCreate = () => {
  const { token } = useGPUInstanceStore();
  const { orgId, workspaceId } = useParamWorkspaceId();
  const { isDemoMode } = useDemoMode();
  const { data: instancePricing, isLoading: pricingLoading } = useGetServerlessInstancePricing();

  // Deep-link intent handling
  const { intent: deepLinkIntent, clearIntent: clearDeepLinkIntent } = useDeepLinkIntent();

  // Credit balance check
  const { data: creditData, isLoading: isLoadingCredit } = useGetUserBalance();
  const totalAvailableCredit = creditData?.balance ?? 0;
  const hasInsufficientBalance = totalAvailableCredit < 0.5;
  const { isOpen, setIsOpen, openTopup, onSuccess } = useQuickTopup();

  // Form state
  const [instanceType, setInstanceType] = useState<string>('');
  const [volumeSize, setVolumeSize] = useState<number>(200);
  const [projectName, setProjectName] = useState<string>('');

  // Base VM configuration state (Demo only)
  const [gpuCount, setGpuCount] = useState<number>(1);
  const [cpuCount, setCpuCount] = useState<number>(8);
  const [ramSize, setRamSize] = useState<number>(64);
  const [selectedImage, setSelectedImage] = useState<string>('ubuntu-22.04-cuda-12.4');
  const [customImageUrl, setCustomImageUrl] = useState<string>('');
  const [isPrivateRegistry, setIsPrivateRegistry] = useState<boolean>(false);
  const [registryUsername, setRegistryUsername] = useState<string>('');
  const [registryPassword, setRegistryPassword] = useState<string>('');
  const [showAdvancedImageOptions, setShowAdvancedImageOptions] = useState<boolean>(false);

  // MIG configuration state - array of configs, one per GPU
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [migConfigs, setMigConfigs] = useState<Array<Record<string, number>>>([]);

  // Check if H200 is selected (demo only)
  const isH200Selected = instanceType === 'NVIDIA H200 141GB';

  // H200 GPU VRAM limit
  const GPU_VRAM_LIMIT = 141;

  // Initialize/resize migConfigs when gpuCount changes
  useEffect(() => {
    setMigConfigs(prev => {
      const newConfigs = [...prev];
      const defaultConfig = { '1g.18gb': 0, '1g.35gb': 0, '2g.35gb': 0, '3g.71gb': 0, '4g.71gb': 0 };
      // Expand array to match gpuCount
      while (newConfigs.length < gpuCount) {
        newConfigs.push({ ...defaultConfig });
      }
      // Shrink array if needed
      return newConfigs.slice(0, gpuCount);
    });
  }, [gpuCount]);

  // Per-GPU validation helpers
  const getGpuMemoryUsed = (gpuIndex: number): number => {
    const config = migConfigs[gpuIndex];
    if (!config) return 0;
    return Object.entries(config).reduce((total, [profileId, count]) => {
      const profile = MIG_PROFILES_FOR_MIXING.find(p => p.id === profileId);
      return total + (profile ? profile.memory * count : 0);
    }, 0);
  };

  const isGpuConfigValid = (gpuIndex: number): boolean => {
    const memory = getGpuMemoryUsed(gpuIndex);
    return memory > 0 && memory <= GPU_VRAM_LIMIT;
  };

  const areAllGpuConfigsValid = (): boolean => {
    return migConfigs.every((_, i) => isGpuConfigValid(i));
  };

  // Handle MIG config count change for a specific GPU
  const handleMigConfigChange = (gpuIndex: number, profileId: string, delta: number) => {
    setMigConfigs(prev => {
      const newConfigs = [...prev];
      const config = { ...newConfigs[gpuIndex] };
      const profile = MIG_PROFILES_FOR_MIXING.find(p => p.id === profileId);
      if (!profile) return prev;

      const currentCount = config[profileId] || 0;
      const newCount = Math.max(0, currentCount + delta);

      config[profileId] = newCount;
      newConfigs[gpuIndex] = config;
      return newConfigs;
    });
    // Clear preset selection when manually adjusting
    setSelectedPreset(null);
  };

  // Apply preset configuration to ALL GPUs
  const applyPresetConfig = (preset: typeof PRESET_MIXED_CONFIGS[0]) => {
    const newConfig: Record<string, number> = {
      '1g.18gb': 0,
      '1g.35gb': 0,
      '2g.35gb': 0,
      '3g.71gb': 0,
      '4g.71gb': 0,
    };
    Object.entries(preset.config).forEach(([key, value]) => {
      newConfig[key] = value;
    });
    // Apply to all GPUs
    setMigConfigs(prev => prev.map(() => ({ ...newConfig })));
    setSelectedPreset(preset.id);
  };

  // Set default instance type to first item when pricing loads
  // In demo mode, default to H200
  useEffect(() => {
    if (!instanceType) {
      if (isDemoMode) {
        setInstanceType('NVIDIA H200 141GB');
      } else if (instancePricing && instancePricing.length > 0) {
        setInstanceType(instancePricing[0].instance_type);
      }
    }
  }, [instancePricing, isDemoMode]);

  // SSE state
  const [taskId, setTaskId] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('idle');
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // SSH connection info
  const [sshHost, setSSHHost] = useState<string | null>(null);
  const [sshPort, setSSHPort] = useState<number | null>(null);
  const [privateKey, setPrivateKey] = useState<string>('');
  const [showKeyModal, setShowKeyModal] = useState<boolean>(false);
  const [isReady, setIsReady] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);

  // Stack deployment state (Demo only - One-Click Deployment)
  const [selectedModels, setSelectedModels] = useState<SelectedModel[]>([]);
  const [stackDeploymentInfo, setStackDeploymentInfo] = useState<StackDeploymentInfo | null>(null);
  const [stackDeploymentStatus, setStackDeploymentStatus] = useState<'idle' | 'deploying' | 'ready'>('idle');

  // Deployment type state (vLLM one-click deploy available in production, others demo-only)
  const [deploymentType, setDeploymentType] = useState<'base-vm' | 'one-click-deploy' | 'ai-training' | 'label' | 'ai-app'>('base-vm');

  // One-Click Deployment framework state (Demo only)
  const [deployFramework, setDeployFramework] = useState<DeploymentFramework>('vllm');

  // AI Training state (Demo only)
  const [selectedFramework, setSelectedFramework] = useState<string>('nvidia-tao');
  const [trainingSetupInfo, setTrainingSetupInfo] = useState<TrainingSetupInfo | null>(null);
  const [trainingSetupStatus, setTrainingSetupStatus] = useState<'idle' | 'setting-up' | 'ready'>('idle');

  // Label tab state (Demo only)
  const [selectedLabelingTool, setSelectedLabelingTool] = useState<LabelingTool>('nvidia-monai');

  // AI App tab state (Demo only)
  const [selectedAIApp, setSelectedAIApp] = useState<AIAppType>('rag');

  const startTimestampRef = useRef<number | null>(null);

  // Apply deep-link intent on mount (one-time behavior)
  useEffect(() => {
    if (!deepLinkIntent) return;

    // Apply deployment type if specified
    if (deepLinkIntent.type === 'one-click-deploy') {
      setDeploymentType('one-click-deploy');
    }

    // Apply model pre-selection if specified
    if (deepLinkIntent.model) {
      // Find the model in DEPLOYABLE_MODELS by id (case-insensitive match)
      const model = DEPLOYABLE_MODELS.find(
        (m) => m.id.toLowerCase() === deepLinkIntent.model?.toLowerCase()
      );

      if (model) {
        // Only pre-select if model framework matches current mode
        // In production, only vLLM models are available
        const isModelAvailable = isDemoMode || model.framework === 'vllm';

        if (isModelAvailable) {
          // Auto-assign MIG profile for the model
          const migProfile = autoAssignMigProfile(model.vramRequired);
          if (migProfile) {
            setSelectedModels([{ model, migProfile }]);
            // Also set deployment type to one-click-deploy if a model is pre-selected
            setDeploymentType('one-click-deploy');
          }
        }
      }
    }

    // Clear the intent after applying (one-time behavior)
    clearDeepLinkIntent();
  }, [deepLinkIntent, isDemoMode, clearDeepLinkIntent]);

  // Auto-update volume size based on selected models (2x total billion parameters)
  useEffect(() => {
    if (deploymentType === 'one-click-deploy' && selectedModels.length > 0) {
      const recommendedSize = calculateRecommendedVolumeSize(selectedModels);
      setVolumeSize(recommendedSize);
    }
  }, [selectedModels, deploymentType]);

  const handleCreate = async () => {
    if (!token) {
      toastSomethingError('Missing authentication token');
      return;
    }

    if (!instanceType) {
      toastSomethingError('Please select an instance type');
      return;
    }

    if (volumeSize < 50 || volumeSize > 10000) {
      toastSomethingError('Volume size must be between 50 and 10000 GB');
      return;
    }

    // Reset state
    setTaskId(null);
    setStatus('starting');
    setStatusMessage('Initializing instance...');
    setError(null);
    setIsRunning(true);
    setSSHHost(null);
    setSSHPort(null);
    setPrivateKey('');
    setIsReady(false);
    // Reset Stack state (but keep model selection)
    setStackDeploymentInfo(null);
    setStackDeploymentStatus('idle');
    // Reset Training state (but keep selection)
    setTrainingSetupInfo(null);
    setTrainingSetupStatus('idle');
    startTimestampRef.current = Date.now();

    try {
      // Build args for vLLM one-click deployment (HuggingFace model IDs)
      const isVllmDeploy = deploymentType === 'one-click-deploy' &&
        (isDemoMode ? deployFramework === 'vllm' : true) &&
        selectedModels.length > 0;

      const vllmArgs = isVllmDeploy
        ? selectedModels.map((sm) => sm.model.modelId).join(',')
        : undefined;

      // Get tool_parser and reasoning_template from selected model (for custom models)
      // Use the first model's values (in production, only one model is selected)
      const selectedModel = selectedModels.length > 0 ? selectedModels[0].model : null;
      const toolParser = isVllmDeploy && selectedModel?.toolParser ? selectedModel.toolParser : undefined;
      const reasoningTemplate = isVllmDeploy && selectedModel?.reasoningTemplate ? selectedModel.reasoningTemplate : undefined;

      // Collect unique abilities from selected models for use_case
      const getUseCase = () => {
        if (deploymentType === 'one-click-deploy' && selectedModels.length > 0) {
          const abilities = selectedModels
            .flatMap((sm) => sm.model.ability || [])
            .filter((ability, index, self) => self.indexOf(ability) === index); // unique
          return abilities.length > 0 ? abilities : undefined;
        }
        return undefined;
      };

      // Determine instance_tag based on deployment type
      const getInstanceTag = (): 'vllm' | 'triton' | 'tao' | 'monai' | 'nemo' | 'float16-training' | undefined => {
        // One-click deployment
        if (deploymentType === 'one-click-deploy' && selectedModels.length > 0) {
          // In demo mode, use the selected framework
          if (isDemoMode) {
            return deployFramework; // 'vllm' | 'triton'
          }
          // In production, only vLLM is available
          return 'vllm';
        }

        // AI Training (demo only)
        if (isDemoMode && deploymentType === 'ai-training') {
          if (selectedFramework === 'nvidia-tao') return 'tao';
          if (selectedFramework === 'nvidia-nemo') return 'nemo';
          if (selectedFramework === 'float16-training') return 'float16-training';
        }

        // Label (demo only - MONAI)
        if (isDemoMode && deploymentType === 'label') {
          if (selectedLabelingTool === 'nvidia-monai') return 'monai';
        }

        return undefined;
      };

      const useCase = getUseCase();

      const response = await runSSHInstanceSSE(
        {
          gpu_type: instanceType,
          volume_size: volumeSize,
          rootfs_size: 500, // Fixed at 500 GB
          ...(projectName.trim() && { project_name: projectName.trim() }),
          ...(isVllmDeploy && { container_image: 'vllm' }),
          ...(vllmArgs && { args: vllmArgs }),
          instance_tag: getInstanceTag(),
          ...(useCase && { use_case: useCase }),
          // vLLM model support overrides (for custom models)
          ...(toolParser && { tool_parser: toolParser }),
          ...(reasoningTemplate && { reasoning_template: reasoningTemplate }),
        },
        token
      );

      if (!response.body) {
        throw new Error('No response body');
      }

      const reader = response.body.getReader();

      for await (const event of parseSSEStream(reader)) {
        switch (event.type) {
          case 'task_id': {
            const data = event.data as SSETaskIdData;
            setTaskId(data.task_id);
            setStatus('running');
            setStatusMessage('Instance created, waiting for SSH key...');
            break;
          }
          case 'ssh_key': {
            const data = event.data as SSESSHKeyData;
            setPrivateKey(data.private_key);
            setShowKeyModal(true);
            setStatusMessage('SSH key received, waiting for instance to be ready...');
            break;
          }
          case 'status': {
            const data = event.data as SSEStatusData;
            setStatus(data.status);
            setStatusMessage(data.message);
            break;
          }
          case 'ssh_ready': {
            setSSHHost('slintprod-1.float16.cloud');
            setSSHPort(2222);
            setIsReady(true);
            setStatusMessage('Instance ready for SSH connection');
            toast.success('Instance is ready!');

            // After SSH ready, simulate stack deployment (Demo only - One-Click Deployment)
            if (isDemoMode && deploymentType === 'one-click-deploy' && selectedModels.length > 0) {
              setStackDeploymentStatus('deploying');
              setStatusMessage(deployFramework === 'triton' ? 'Deploying models...' : 'Deploying models with MIG...');

              // Simulate deployment progress (3 seconds)
              setTimeout(() => {
                const info = mockStackDeploymentInfo(taskId!, selectedModels, deployFramework);
                setStackDeploymentInfo(info);
                setStackDeploymentStatus('ready');
                setStatusMessage('Models deployed successfully');
                toast.success('Models are ready!');
              }, 3000);
            }

            // After SSH ready, simulate training setup (Demo only)
            if (isDemoMode && deploymentType === 'ai-training') {
              setTrainingSetupStatus('setting-up');
              setStatusMessage('Setting up training environment...');

              // Simulate setup progress (3 seconds)
              setTimeout(() => {
                const info = mockTrainingSetupInfo(taskId!, selectedFramework, '');
                setTrainingSetupInfo(info);
                setTrainingSetupStatus('ready');
                setStatusMessage('Training environment ready');
                toast.success('Training environment is ready!');
              }, 3000);
            }
            break;
          }
          case 'complete': {
            const data = event.data as SSECompleteData;
            setStatus('completed');
            setStatusMessage(data.finish_reason || 'Session ended');
            setIsRunning(false);
            break;
          }
          case 'error': {
            const data = event.data as SSEErrorData;
            setStatus('error');
            setError(data.error);
            setStatusMessage(data.message || data.error);
            setIsRunning(false);
            toastSomethingError(data.error);
            break;
          }
        }
      }

      if (isRunning) {
        setIsRunning(false);
      }
    } catch (err: any) {
      setStatus('error');
      setError(err.message || 'Unknown error');
      setStatusMessage(err.message || 'Unknown error');
      setIsRunning(false);
      toastSomethingError(err.message);
    }
  };

  const handleCopySSHCommand = async () => {
    if (!sshHost || !sshPort || !taskId) return;
    const command = `ssh -i gpu-instance-${taskId}.pem ${taskId}@${sshHost} -p ${sshPort}`;
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const selectedPricing = instancePricing?.find(
    (p) => p.instance_type === instanceType
  );

  return (
    <div className="container py-8 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-6 text-center">Create GPU Instance</h1>

      <div className="space-y-6">
        {/* Insufficient Balance Warning */}
        {!isLoadingCredit && hasInsufficientBalance && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Insufficient Credit Balance</AlertTitle>
            <AlertDescription className="mt-2">
              <p>
                Your current balance is {fCurrencyUSDExtended(totalAvailableCredit)}.
                Please top up your account to create GPU instances.
              </p>
              <div className="mt-2 flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={openTopup}>
                  Quick Top-up
                </Button>
                {(() => {
                  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                  const isThai = tz === 'Asia/Bangkok' || tz === 'Asia/Phnom_Penh' || tz === 'Asia/Vientiane' || navigator.language.startsWith('th');
                  return isThai ? (
                    /* Official PromptPay logo */
                    <svg width="68" height="20" viewBox="0 0 68 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="PromptPay">
                      <rect width="68" height="20" rx="3" fill="#1A2B5F"/>
                      {/* QR mark */}
                      <rect x="3" y="3" width="5" height="5" rx="0.5" stroke="white" strokeWidth="1"/>
                      <rect x="4.5" y="4.5" width="2" height="2" fill="white"/>
                      <rect x="3" y="12" width="5" height="5" rx="0.5" stroke="white" strokeWidth="1"/>
                      <rect x="4.5" y="13.5" width="2" height="2" fill="white"/>
                      <rect x="12" y="3" width="5" height="5" rx="0.5" stroke="white" strokeWidth="1"/>
                      <rect x="13.5" y="4.5" width="2" height="2" fill="white"/>
                      <rect x="12" y="12" width="1.5" height="1.5" fill="white"/>
                      <rect x="14.5" y="12" width="1.5" height="1.5" fill="white"/>
                      <rect x="12" y="14.5" width="1.5" height="1.5" fill="white"/>
                      <rect x="14.5" y="14.5" width="1.5" height="1.5" fill="white"/>
                      <rect x="9" y="9" width="1.5" height="1.5" fill="white"/>
                      <rect x="9" y="3" width="1.5" height="7" fill="white"/>
                      <rect x="3" y="9" width="7" height="1.5" fill="white"/>
                      {/* "PromptPay" wordmark */}
                      <text x="20" y="14" fontFamily="Arial, sans-serif" fontSize="7.5" fontWeight="bold" fill="white" letterSpacing="0.2">PromptPay</text>
                    </svg>
                  ) : null;
                })()}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Configuration Form */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Instance Configuration</CardTitle>
                <CardDescription>
                  Configure your GPU instance settings
                </CardDescription>
              </div>
              {isDemoMode && (
                <Badge variant="secondary">Demo</Badge>
              )}
            </div>
            {/* Deployment Type Tabs */}
            <Tabs
              value={deploymentType}
              onValueChange={(v) => setDeploymentType(v as typeof deploymentType)}
              className="mt-4"
            >
              <TabsList variant="outline" className="w-full justify-start">
                <TabsTrigger value="base-vm" variant="outline" disabled={isRunning}>
                  Base VM
                </TabsTrigger>
                <TabsTrigger value="one-click-deploy" variant="outline" disabled={isRunning}>
                  One-Click Deployment
                </TabsTrigger>
                {isDemoMode && (
                  <>
                    <TabsTrigger value="ai-training" variant="outline" disabled={isRunning}>
                      AI Training
                    </TabsTrigger>
                    <TabsTrigger value="label" variant="outline" disabled={isRunning}>
                      Label
                    </TabsTrigger>
                    <TabsTrigger value="ai-app" variant="outline" disabled={isRunning}>
                      AI App
                    </TabsTrigger>
                  </>
                )}
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Project Name */}
            <div className="space-y-2">
              <Label htmlFor="project-name">Project Name (Optional)</Label>
              <Input
                id="project-name"
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="e.g. my-dev-server"
                disabled={isRunning}
              />
              <p className="text-sm text-muted-foreground">
                A friendly name for your project
              </p>
            </div>

            {/* Instance Type */}
            <div className="space-y-2">
              <Label htmlFor="instance-type">Instance Type</Label>
              <Select
                value={instanceType}
                onValueChange={setInstanceType}
                disabled={isRunning}
              >
                <SelectTrigger id="instance-type">
                  <SelectValue placeholder="Select instance type" />
                </SelectTrigger>
                <SelectContent>
                  {pricingLoading ? (
                    <SelectItem value="loading" disabled>
                      Loading...
                    </SelectItem>
                  ) : (
                    <>
                      {instancePricing?.map((instance) => (
                        <SelectItem
                          key={instance.instance_id}
                          value={instance.instance_type}
                        >
                          {instance.instance_type} - ${instance.price_per_hour}/hr
                          ({instance.instance_provider})
                        </SelectItem>
                      ))}
                      {/* Demo-only: H200 option */}
                      {isDemoMode && (
                        <SelectItem value="NVIDIA H200 141GB">
                          NVIDIA H200 141GB - $12.00/hr (Demo)
                        </SelectItem>
                      )}
                    </>
                  )}
                </SelectContent>
              </Select>
              {selectedPricing && (
                <p className="text-sm text-muted-foreground">
                  Region: {selectedPricing.instance_region} | Provider:{' '}
                  {selectedPricing.instance_provider}
                </p>
              )}
              {isH200Selected && (
                <p className="text-sm text-muted-foreground">
                  Region: Thailand | Provider: Float16 (Demo)
                </p>
              )}
            </div>

            {/* Demo-only: Base VM Configuration (GPU, CPU, RAM, Image) */}
            {isDemoMode && deploymentType === 'base-vm' && (
              <>
                {/* GPU Count Configuration */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="gpu-count">Number of GPUs</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="gpu-count"
                        type="number"
                        value={gpuCount}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          if (value >= GPU_COUNT_CONFIG.min && value <= GPU_COUNT_CONFIG.max) {
                            setGpuCount(value);
                          }
                        }}
                        min={GPU_COUNT_CONFIG.min}
                        max={GPU_COUNT_CONFIG.max}
                        step={GPU_COUNT_CONFIG.step}
                        disabled={isRunning}
                        className="w-20 h-8 text-right"
                      />
                      <span className="text-sm text-muted-foreground w-12">GPUs</span>
                    </div>
                  </div>
                  <Slider
                    value={[gpuCount]}
                    min={GPU_COUNT_CONFIG.min}
                    max={GPU_COUNT_CONFIG.max}
                    step={GPU_COUNT_CONFIG.step}
                    onValueChange={([value]) => setGpuCount(value)}
                    disabled={isRunning}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{GPU_COUNT_CONFIG.min} GPU</span>
                    <span>{GPU_COUNT_CONFIG.max} GPUs</span>
                  </div>
                  {gpuCount > 1 && (
                    <p className="text-sm text-muted-foreground">
                      {gpuCount}x {instanceType || 'GPU'} with NVLink interconnect
                    </p>
                  )}
                </div>

                {/* CPU Configuration */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="cpu-count">CPU (vCPUs)</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="cpu-count"
                        type="number"
                        value={cpuCount}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          if (value >= CPU_CONFIG.min && value <= CPU_CONFIG.max) {
                            setCpuCount(value);
                          }
                        }}
                        min={CPU_CONFIG.min}
                        max={CPU_CONFIG.max}
                        step={CPU_CONFIG.step}
                        disabled={isRunning}
                        className="w-20 h-8 text-right"
                      />
                      <span className="text-sm text-muted-foreground w-12">vCPUs</span>
                    </div>
                  </div>
                  <Slider
                    value={[cpuCount]}
                    min={CPU_CONFIG.min}
                    max={CPU_CONFIG.max}
                    step={CPU_CONFIG.step}
                    onValueChange={([value]) => setCpuCount(value)}
                    disabled={isRunning}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{CPU_CONFIG.min} vCPU</span>
                    <span>{CPU_CONFIG.max} vCPUs</span>
                  </div>
                </div>

                {/* RAM Configuration */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="ram-size">RAM (GB)</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="ram-size"
                        type="number"
                        value={ramSize}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          if (value >= RAM_CONFIG.min && value <= RAM_CONFIG.max) {
                            setRamSize(value);
                          }
                        }}
                        min={RAM_CONFIG.min}
                        max={RAM_CONFIG.max}
                        step={RAM_CONFIG.step}
                        disabled={isRunning}
                        className="w-20 h-8 text-right"
                      />
                      <span className="text-sm text-muted-foreground w-12">GB</span>
                    </div>
                  </div>
                  <Slider
                    value={[ramSize]}
                    min={RAM_CONFIG.min}
                    max={RAM_CONFIG.max}
                    step={RAM_CONFIG.step}
                    onValueChange={([value]) => setRamSize(value)}
                    disabled={isRunning}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{RAM_CONFIG.min} GB</span>
                    <span>{RAM_CONFIG.max} GB</span>
                  </div>
                </div>

                {/* Image Selection */}
                <div className="space-y-3">
                  <Label htmlFor="image">Image</Label>
                  <Select
                    value={selectedImage}
                    onValueChange={(value) => {
                      setSelectedImage(value);
                      if (value !== 'custom') {
                        setCustomImageUrl('');
                        setIsPrivateRegistry(false);
                        setRegistryUsername('');
                        setRegistryPassword('');
                      }
                    }}
                    disabled={isRunning}
                  >
                    <SelectTrigger id="image">
                      <SelectValue placeholder="Select an image" />
                    </SelectTrigger>
                    <SelectContent>
                      {IMAGE_OPTIONS.map((image) => (
                        <SelectItem key={image.id} value={image.id}>
                          {image.name}
                          {image.recommended && ' (Recommended)'}
                        </SelectItem>
                      ))}
                      <SelectItem value="custom">Custom Image</SelectItem>
                    </SelectContent>
                  </Select>
                  {selectedImage && selectedImage !== 'custom' && (
                    <p className="text-sm text-muted-foreground">
                      {IMAGE_OPTIONS.find((img) => img.id === selectedImage)?.description}
                    </p>
                  )}
                  {selectedImage === 'custom' && (
                    <div className="space-y-4 mt-2">
                      <div className="space-y-2">
                        <Label htmlFor="custom-image-url">Image URL</Label>
                        <Input
                          id="custom-image-url"
                          type="text"
                          value={customImageUrl}
                          onChange={(e) => setCustomImageUrl(e.target.value)}
                          placeholder="e.g. nvcr.io/nvidia/pytorch:24.01-py3"
                          disabled={isRunning}
                        />
                        <p className="text-sm text-muted-foreground">
                          Docker Hub, NVIDIA NGC, AWS ECR, GCR, or any container registry
                        </p>
                      </div>

                      {/* Advanced Options Toggle */}
                      <div
                        className="flex items-center justify-between cursor-pointer py-2"
                        onClick={() => !isRunning && setShowAdvancedImageOptions(!showAdvancedImageOptions)}
                      >
                        <span className="text-sm font-medium">Advanced Options</span>
                        <Badge variant="outline" className="text-xs">
                          {showAdvancedImageOptions ? 'Hide' : 'Show'}
                        </Badge>
                      </div>

                      {/* Advanced Options Content */}
                      {showAdvancedImageOptions && (
                        <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
                          {/* Private Registry Toggle */}
                          <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                              <Label htmlFor="private-registry">Private Registry</Label>
                              <p className="text-xs text-muted-foreground">
                                Enable if image requires authentication
                              </p>
                            </div>
                            <Switch
                              id="private-registry"
                              checked={isPrivateRegistry}
                              onCheckedChange={(checked) => {
                                setIsPrivateRegistry(checked);
                                if (!checked) {
                                  setRegistryUsername('');
                                  setRegistryPassword('');
                                }
                              }}
                              disabled={isRunning}
                            />
                          </div>

                          {/* Registry Credentials */}
                          {isPrivateRegistry && (
                            <div className="space-y-3 pt-2 border-t">
                              <div className="space-y-2">
                                <Label htmlFor="registry-username">Username / Access Key</Label>
                                <Input
                                  id="registry-username"
                                  type="text"
                                  value={registryUsername}
                                  onChange={(e) => setRegistryUsername(e.target.value)}
                                  placeholder="e.g. $oauthtoken, AWS, _json_key"
                                  disabled={isRunning}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="registry-password">Password / Token</Label>
                                <Input
                                  id="registry-password"
                                  type="password"
                                  value={registryPassword}
                                  onChange={(e) => setRegistryPassword(e.target.value)}
                                  placeholder="API key, password, or access token"
                                  disabled={isRunning}
                                />
                              </div>
                              <Alert>
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription className="text-xs">
                                  <strong>Common formats:</strong><br />
                                  • NGC: username = <code>$oauthtoken</code>, password = NGC API Key<br />
                                  • Docker Hub: username + access token<br />
                                  • AWS ECR: username = <code>AWS</code>, password = ECR token<br />
                                  • GCR: username = <code>_json_key</code>, password = service account JSON
                                </AlertDescription>
                              </Alert>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Demo-only: MIG Configuration for H200 (Base VM only) */}
            <DemoOnly>
              {isH200Selected && deploymentType === 'base-vm' && migConfigs.length > 0 && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>MIG Configuration</Label>
                    <p className="text-sm text-muted-foreground">
                      Configure MIG profiles for each GPU. Memory must not exceed {GPU_VRAM_LIMIT}GB per GPU.
                    </p>
                  </div>

                  {/* Quick Presets - applies to all GPUs */}
                  <div className="space-y-2">
                    <Label className="text-sm">Quick Presets (applies to all GPUs)</Label>
                    <div className="flex flex-wrap gap-2">
                      {PRESET_MIXED_CONFIGS.map((preset) => (
                        <Button
                          key={preset.id}
                          variant={selectedPreset === preset.id ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => applyPresetConfig(preset)}
                          disabled={isRunning}
                          className="text-xs"
                        >
                          {preset.name}
                          {preset.isRecommended && !selectedPreset && ' *'}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* MIG Configuration Table */}
                  <Card>
                    <CardContent className="pt-4">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-2 px-2 font-medium">GPU</th>
                              {MIG_PROFILES_FOR_MIXING.map((profile) => (
                                <th key={profile.id} className="text-center py-2 px-1 font-medium">
                                  <div className="flex flex-col items-center">
                                    <span className="font-mono text-xs">{profile.name}</span>
                                    <span className="text-xs text-muted-foreground font-normal">{profile.memory}GB</span>
                                  </div>
                                </th>
                              ))}
                              <th className="text-center py-2 px-2 font-medium">Memory</th>
                              <th className="text-center py-2 px-2 font-medium">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {migConfigs.map((config, gpuIndex) => {
                              const memoryUsed = getGpuMemoryUsed(gpuIndex);
                              const isValid = isGpuConfigValid(gpuIndex);

                              return (
                                <tr key={gpuIndex} className="border-b last:border-0">
                                  <td className="py-3 px-2">
                                    <span className="font-medium">GPU {gpuIndex}</span>
                                  </td>
                                  {MIG_PROFILES_FOR_MIXING.map((profile) => {
                                    const count = config[profile.id] || 0;

                                    return (
                                      <td key={profile.id} className="py-2 px-1">
                                        <div className="flex items-center justify-center gap-1">
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6"
                                            onClick={() => handleMigConfigChange(gpuIndex, profile.id, -1)}
                                            disabled={count === 0 || isRunning}
                                          >
                                            <Minus className="h-3 w-3" />
                                          </Button>
                                          <span className="w-6 text-center font-mono">{count}</span>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6"
                                            onClick={() => handleMigConfigChange(gpuIndex, profile.id, 1)}
                                            disabled={isRunning}
                                          >
                                            <Plus className="h-3 w-3" />
                                          </Button>
                                        </div>
                                      </td>
                                    );
                                  })}
                                  <td className="py-2 px-2 text-center">
                                    <span className={`font-mono text-xs ${memoryUsed > GPU_VRAM_LIMIT ? 'text-destructive' : ''}`}>
                                      {memoryUsed}/{GPU_VRAM_LIMIT}
                                    </span>
                                  </td>
                                  <td className="py-2 px-2 text-center">
                                    {isValid ? (
                                      <Badge className="!bg-green-600 !text-white !hover:bg-green-700 text-xs">
                                        <Check className="h-3 w-3 mr-1" />
                                        Valid
                                      </Badge>
                                    ) : memoryUsed === 0 ? (
                                      <Badge variant="secondary" className="text-xs">
                                        Empty
                                      </Badge>
                                    ) : (
                                      <Badge variant="destructive" className="text-xs">
                                        Over {GPU_VRAM_LIMIT}GB
                                      </Badge>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Overall validation status */}
                      <div className="mt-4 pt-4 border-t">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Overall Configuration</span>
                          {areAllGpuConfigsValid() ? (
                            <Badge className="!bg-green-600 !text-white hover:!bg-green-700">All GPUs Valid</Badge>
                          ) : (
                            <Badge variant="destructive">Configuration Incomplete</Badge>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </DemoOnly>

            {/* Framework & Model Selection (One-Click Deployment tab) */}
            {/* vLLM available in production, Triton demo-only */}
            {deploymentType === 'one-click-deploy' && (
              <div className="space-y-6">
                {isDemoMode ? (
                  <FrameworkSelector
                    selectedFramework={deployFramework}
                    onFrameworkChange={(framework) => {
                      setDeployFramework(framework);
                      setSelectedModels([]); // Clear models when framework changes
                    }}
                    disabled={isRunning}
                  />
                ) : (
                  // In production, only vLLM is available
                  <div className="space-y-2">
                    <Label className="text-base font-medium">Deployment Framework</Label>
                    <p className="text-sm text-muted-foreground">
                      vLLM - High-throughput LLM serving with PagedAttention
                    </p>
                  </div>
                )}
                <ModelMultiSelector
                  framework={isDemoMode ? deployFramework : 'vllm'}
                  selectedModels={selectedModels}
                  onSelectionChange={setSelectedModels}
                  disabled={isRunning}
                  supportsMIG={isDemoMode && deployFramework !== 'triton'}
                  singleSelection={!isDemoMode}
                />
              </div>
            )}

            {/* Training Configuration (Demo only - AI Training tab) */}
            {isDemoMode && deploymentType === 'ai-training' && (
              <TrainingConfigSelector
                selectedFramework={selectedFramework}
                onFrameworkSelect={setSelectedFramework}
                disabled={isRunning}
              />
            )}

            {/* Label Configuration (Demo only - Label tab) */}
            {isDemoMode && deploymentType === 'label' && (
              <LabelConfigSelector
                selectedTool={selectedLabelingTool}
                onToolChange={setSelectedLabelingTool}
                disabled={isRunning}
              />
            )}

            {/* AI App Selection (Demo only - AI App tab) */}
            {isDemoMode && deploymentType === 'ai-app' && (
              <AIAppSelector
                selectedApp={selectedAIApp}
                onAppChange={setSelectedAIApp}
                disabled={isRunning}
              />
            )}

            {/* Volume Size */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="volume-size">Volume Size (GB)</Label>
                {deploymentType === 'one-click-deploy' && selectedModels.length > 0 && (
                  <Badge variant="outline" className="text-xs">Auto-calculated</Badge>
                )}
              </div>
              <Input
                id="volume-size"
                type="number"
                value={volumeSize}
                onChange={(e) => {
                  const value = e.target.valueAsNumber;
                  setVolumeSize(isNaN(value) ? 0 : value);
                }}
                min={50}
                max={10000}
                disabled={isRunning}
              />
              <p className="text-sm text-muted-foreground">
                {deploymentType === 'one-click-deploy' && selectedModels.length > 0
                  ? 'Auto-calculated: 2x total billion parameters (minimum 100GB)'
                  : 'Persistent storage for your data (50-10000 GB)'}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              {hasInsufficientBalance ? (
                <Button
                  onClick={openTopup}
                  className="flex-1"
                >
                  <CreditCard className="size-4 mr-2" />
                  Topup & Create Instance
                </Button>
              ) : (
                <Button
                  onClick={handleCreate}
                  disabled={isRunning || !instanceType}
                  className="flex-1"
                >
                  {isRunning ? (
                    <Loader2 className="size-4 mr-2 animate-spin" />
                  ) : (
                    <PlayIcon className="size-4 mr-2" />
                  )}
                  {isRunning ? 'Creating...' : 'Create Instance'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Status Card */}
        {(taskId || isRunning) && (
            <Card>
              <CardHeader>
                <CardTitle>Instance Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {taskId && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Task ID</span>
                    <code className="text-sm bg-muted px-2 py-1 rounded">
                      {taskId}
                    </code>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Status</span>
                  <span className="font-medium capitalize">{status}</span>
                </div>
                {statusMessage && (
                  <div className="text-sm text-muted-foreground">
                    {statusMessage}
                  </div>
                )}
                {isRunning && (
                  <div className="flex items-center gap-2">
                    <Loader2 className="size-4 animate-spin" />
                    <span className="text-sm">Processing...</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

        {/* Error Display */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* SSH Connection Info */}
        {isReady && sshHost && sshPort && (
            <Card className="border-green-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-green-600">
                  <Terminal className="size-5" />
                  SSH Connection Ready
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Host</span>
                    <code className="text-sm bg-muted px-2 py-1 rounded">
                      {sshHost}
                    </code>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Port</span>
                    <code className="text-sm bg-muted px-2 py-1 rounded">
                      {sshPort}
                    </code>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>SSH Command</Label>
                  <div className="relative">
                    <pre className="bg-muted p-3 rounded-lg text-sm whitespace-pre-wrap break-all">
{`ssh -i gpu-instance-${taskId}.pem \\
  ${taskId}@${sshHost} -p ${sshPort}`}
                    </pre>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="absolute top-1 right-1 h-8"
                      onClick={handleCopySSHCommand}
                    >
                      {copied ? (
                        <Check className="size-4" />
                      ) : (
                        <Copy className="size-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <Alert>
                  <AlertDescription>
                    Make sure you have saved your SSH private key before
                    connecting. Use <code>chmod 400</code> to set correct
                    permissions on the key file.
                  </AlertDescription>
                </Alert>

                <Alert>
                  <AlertDescription>
                    To manage or terminate this instance, go to{' '}
                    <Link to={PATH_PAGE.dashboard.gpuInstance.instances(orgId, workspaceId)}>
                      <Button variant="link" className="p-0 h-auto">GPU Instances</Button>
                    </Link>
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
        )}

        {/* Stack Deployment Info (Demo only - One-Click Deployment) */}
        {isDemoMode && stackDeploymentStatus === 'deploying' && (
          <Card className="border-blue-500">
            <CardContent className="flex items-center gap-3 py-6">
              <Loader2 className="size-5 animate-spin text-blue-600" />
              <span className="text-sm">
                {deployFramework === 'triton'
                  ? 'Deploying AI stack...'
                  : 'Deploying AI stack with MIG...'}
              </span>
            </CardContent>
          </Card>
        )}

        {isDemoMode && stackDeploymentStatus === 'ready' && stackDeploymentInfo && (
          <StackDeploymentInfoCard
            deploymentInfo={stackDeploymentInfo}
            supportsMIG={deployFramework !== 'triton'}
          />
        )}

        {/* Training Setup Info (Demo only) */}
        {isDemoMode && trainingSetupStatus === 'setting-up' && (
          <Card className="border-blue-500">
            <CardContent className="flex items-center gap-3 py-6">
              <Loader2 className="size-5 animate-spin text-blue-600" />
              <span className="text-sm">Setting up training environment...</span>
            </CardContent>
          </Card>
        )}

        {isDemoMode && trainingSetupStatus === 'ready' && trainingSetupInfo && (
          <TrainingSetupInfoCard setupInfo={trainingSetupInfo} />
        )}
      </div>

      {/* SSH Key Modal */}
      <SSHKeyModal
        isOpen={showKeyModal}
        onClose={() => setShowKeyModal(false)}
        privateKey={privateKey}
        taskId={taskId || 'unknown'}
      />

      <QuickTopupDialog open={isOpen} onOpenChange={setIsOpen} onSuccess={onSuccess} />
    </div>
  );
};

export default GPUInstanceCreate;
